/**
 * Resolves verified file gateway URLs for previews and downloads.
 * Completely replaces direct S3, MinIO, or local disk paths.
 */

export interface FileAttachmentMeta {
  file_id?: string;
  id?: string;
  org_id?: string;
  project_id?: string;
  table_id?: string;
  filename?: string;
  content_type?: string;
  size_bytes?: number;
  public_url?: string;
  gateway_url?: string;
}

export function isFileAttachment(val: any): boolean {
  if (!val || typeof val !== 'object') return false;
  return Boolean(
    val.file_id ||
      val.gateway_url ||
      (val.filename && (val.size_bytes !== undefined || val.content_type || val.public_url))
  );
}

export function formatFileSize(bytes?: number): string {
  if (bytes === undefined || bytes === null || isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}

export function resolveFileGatewayUrl(
  file: FileAttachmentMeta | any,
  fallbackOrgId?: string
): { gatewayUrl: string; downloadUrl: string; previewUrl: string } {
  // If the file already carries a valid signed HMAC gateway or public URL, use it directly
  const signedUrl =
    file?.gateway_url && file.gateway_url.includes('sig=')
      ? file.gateway_url
      : file?.public_url && file.public_url.includes('sig=')
      ? file.public_url
      : '';

  if (signedUrl) {
    const sep = signedUrl.includes('?') ? '&' : '?';
    return {
      gatewayUrl: signedUrl,
      downloadUrl: signedUrl,
      previewUrl: `${signedUrl}${sep}preview=1`,
    };
  }

  const fileId = file?.file_id || file?.id;
  let orgId = file?.org_id;

  // Try extracting org_id from public_url if available (e.g. /orgs/{org_id}/ or /files/{org_id}/)
  if (!orgId && file?.public_url) {
    const match = file.public_url.match(/\/(?:orgs|files)\/([0-9a-fA-F-]{36})\//);
    if (match) {
      orgId = match[1];
    }
  }

  if (!orgId && fallbackOrgId) {
    orgId = fallbackOrgId;
  }

  const token = localStorage.getItem('ericius_auth_token') || '';

  if (orgId && fileId) {
    const base = `/api/v1/files/${orgId}/${fileId}`;
    return {
      gatewayUrl: base,
      downloadUrl: token ? `${base}?token=${token}` : base,
      previewUrl: token ? `${base}?token=${token}&preview=1` : `${base}?preview=1`,
    };
  }

  // Fallback to public_url or '#'
  const fallback = file?.public_url || '#';
  return {
    gatewayUrl: fallback,
    downloadUrl: fallback,
    previewUrl: fallback,
  };
}
